import { TradeVenue } from '@/types/generated/common/types'
import type { ApiKeyGuide } from '@/types/generated/public_api'

/**
 * Last-resort copy of the API-key onboarding instructions, used ONLY while
 * `get_venues` is still in flight or against a public-api that predates
 * `VenueInfo.apiKeyGuide`.
 *
 * The served list is authoritative: it comes from the same config the backend
 * checks the key against, so it cannot drift from what is enforced, and it is
 * environment-aware in ways this table cannot be — dev serves testnet hosts,
 * and Deribit needs one scope fewer there because testnet has no block-RFQ
 * desk.
 *
 * Delete this table once the backend change is deployed everywhere the
 * frontend talks to.
 */
export const FALLBACK_API_KEY_GUIDES: Partial<Record<TradeVenue, ApiKeyGuide>> = {
  [TradeVenue.TRADE_VENUE_DERIBIT]: {
    createKeyUrl: 'https://www.deribit.com/account/BTC/API',
    permissions: [
      { label: 'Account: read', scope: 'account:read' },
      { label: 'Trade: read_write', scope: 'trade:read_write' },
      { label: 'Wallet: read', scope: 'wallet:read' },
      { label: 'Block RFQ: read_write', scope: 'block_rfq:read_write' },
    ],
  },
  [TradeVenue.TRADE_VENUE_BYBIT]: {
    createKeyUrl: 'https://www.bybit.com/app/user/api-management',
    permissions: [
      { label: 'Account type: UNIFIED' },
      { label: 'Trade: read_write' },
      { label: 'Wallet: read' },
    ],
  },
  [TradeVenue.TRADE_VENUE_UPBIT]: {
    createKeyUrl: 'https://www.upbit.com/mypage/open_api_management',
    guideUrl: 'https://www.upbit.com/service_center/open_api_guide',
    permissions: [
      { label: 'Manage Pocket' },
      { label: 'View Account' },
      { label: 'View Orders' },
      { label: 'Make Orders' },
      { label: 'View Withdrawals' },
      { label: 'View Deposits' },
      { label: 'Deposit' },
    ],
    note: 'Select every permission except Withdraw.',
  },
  [TradeVenue.TRADE_VENUE_TOSS]: {
    createKeyUrl: 'https://developers.tossinvest.com',
    permissions: [{ label: 'Account read' }, { label: 'Order read/write' }],
    note: 'Issue the client ID and secret from Open API settings in your Toss Securities account. The secret is shown once. There is no sandbox — these are production keys.',
  },
}

/**
 * Pick the guide to render: whatever `get_venues` served, else the fallback
 * for that venue, else nothing.
 *
 * Deliberately has no cross-venue default. The table this replaces ended in
 * `?? DERIBIT_API_KEY_GUIDE`, so any venue without its own entry showed
 * Deribit's permissions — that is how Toss users were told to grant
 * `block_rfq:read_write`. An unknown venue must render nothing instead.
 */
export function resolveApiKeyGuide(
  served: ApiKeyGuide | undefined,
  venue: TradeVenue
): ApiKeyGuide | undefined {
  return served ?? FALLBACK_API_KEY_GUIDES[venue]
}
